/**
 * Where the app is running, and what it should talk to.
 *
 * One build ships as both the web app and the payload the Tauri desktop shell
 * wraps, so this is the only module that knows the difference.
 *
 * The distinction that matters is the request origin. On the web, the API is
 * same-origin — proxied in dev, served alongside the bundle in production — so
 * a relative path is correct. Inside Tauri the page is loaded from the bundle
 * at `tauri://localhost`, where a relative path resolves to nothing at all.
 * The shell therefore hands over an absolute origin at boot.
 *
 * Boot data arrives synchronously on `window.__AEGIS_DESKTOP__`, injected
 * before any module runs, because the first request must not have to wait for
 * an async handshake to know where to go.
 */

export interface DesktopBoot {
  readonly apiBaseUrl: string;
  readonly websocketUrl: string;
  /** The registered device id. A licensing artifact — see docs/02 Constraint 6. */
  readonly deviceId: string;
  readonly platform: 'windows' | 'macos' | 'linux';
  readonly label: string;
  readonly appVersion: string;
  readonly isDesktop: true;
}

declare global {
  interface Window {
    __AEGIS_DESKTOP__?: DesktopBoot;
  }
}

export function desktopBoot(): DesktopBoot | null {
  return typeof window === 'undefined' ? null : window.__AEGIS_DESKTOP__ ?? null;
}

export function isDesktop(): boolean {
  return desktopBoot() !== null;
}

/**
 * Prefix for every API path. Empty string on the web — a relative path is not
 * a fallback there, it is the correct answer.
 */
export function apiBase(): string {
  return desktopBoot()?.apiBaseUrl ?? '';
}

export function url(path: string): string {
  return `${apiBase()}${path}`;
}

/** The feed endpoint, derived from wherever the API is. */
export function feedUrl(): string {
  const boot = desktopBoot();
  if (boot) return boot.websocketUrl;
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${location.host}/ws`;
}

export type Platform = 'windows' | 'macos' | 'linux' | 'web';

export function platform(): Platform {
  return desktopBoot()?.platform ?? 'web';
}

export function appVersion(): string {
  return desktopBoot()?.appVersion ?? 'web';
}
